import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Search, MapPin, CalendarDays, Clock, ChevronRight, Users, Shield, Zap, Star, Award } from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { HomePageSEO } from '@/components/SEOHead';
import { sportBranches } from '@/data/turkeyData';
import { FACILITY_TYPES, getFacilityTypeLabel } from '@/data/facilityTypes';

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/(api)?\/?$/, "")}/api`;

const HomePage = () => {
  const navigate = useNavigate();
  const [searchCity, setSearchCity] = useState('');
  const [searchType, setSearchType] = useState('');
  const [searchBranch, setSearchBranch] = useState('');
  const [searchDate, setSearchDate] = useState(null);
  const [popularFacilities, setPopularFacilities] = useState([]);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [facilitiesRes, citiesRes] = await Promise.all([
        axios.get(`${API}/facilities/popular`),
        axios.get(`${API}/cities`)
      ]);
      setPopularFacilities(facilitiesRes.data);
      setCities(citiesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchCity) params.set('city', searchCity);
    if (searchType) params.set('type', searchType);
    if (searchBranch) params.set('branch', searchBranch);
    if (searchDate) params.set('date', format(searchDate, 'yyyy-MM-dd'));
    navigate(`/tesisler?${params.toString()}`);
  };

  const facilityTypes = FACILITY_TYPES;

  return (
    <div className="flex flex-col">
      {/* SEO Head */}
      <HomePageSEO />
      
      {/* Hero Section */}
      <section className="hero-section" data-testid="hero-section">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in">
              Spor Tesisini Bul, <br />
              <span className="text-lime-300">Hemen Rezerve Et</span>
            </h1>
            <p className="text-xl text-emerald-100 mb-8 animate-fade-in stagger-1">
              Halı saha, kapalı spor salonu ve daha fazlasını kolayca bulun ve anında rezervasyon yapın.
            </p>
          </div>

          {/* Search Bar */}
          <div className="bg-white rounded-2xl shadow-2xl p-4 md:p-6 max-w-5xl animate-slide-up stagger-2" data-testid="search-bar">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {/* City */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Şehir</label>
                <Select value={searchCity} onValueChange={setSearchCity}>
                  <SelectTrigger data-testid="search-city-select" className="w-full">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-slate-400" />
                      <SelectValue placeholder="Şehir seçin" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {cities.map(city => (
                      <SelectItem key={city} value={city}>
                        {city}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Tesis Türü</label>
                <Select value={searchType} onValueChange={setSearchType}>
                  <SelectTrigger data-testid="search-type-select">
                    <SelectValue placeholder="Tür seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {facilityTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Branch */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Spor Dalı</label>
                <Select value={searchBranch} onValueChange={setSearchBranch}>
                  <SelectTrigger data-testid="search-branch-select">
                    <SelectValue placeholder="Branş seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {sportBranches.map(branch => (
                      <SelectItem key={branch.id} value={branch.id}>
                        {branch.icon} {branch.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Date - REMOVED */}
              {/* <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Tarih</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="w-full justify-start text-left font-normal"
                      data-testid="search-date-picker"
                    >
                      <CalendarDays className="mr-2 h-4 w-4" />
                      {searchDate ? format(searchDate, 'd MMM', { locale: tr }) : 'Tarih'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={searchDate}
                      onSelect={setSearchDate}
                      disabled={(date) => date < new Date()}
                      locale={tr}
                    />
                  </PopoverContent>
                </Popover>
              </div> */}

              {/* Search Button */}
              <div className="flex items-end col-span-2 md:col-span-2">
                <Button 
                  onClick={handleSearch}
                  className="w-full bg-emerald-700 hover:bg-emerald-800 text-white h-11 rounded-xl"
                  data-testid="search-submit-btn"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Ara
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-24 bg-slate-50" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4">
              Nasıl Çalışır?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Üç kolay adımda tesis kiralayın
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="text-center p-6 animate-fade-in" data-testid="step-1">
              <div className="step-circle mx-auto mb-6">
                <Search className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">1. Tesis Bul</h3>
              <p className="text-slate-600">
                Şehir, tür ve tarih filtreleriyle size en uygun tesisi bulun.
              </p>
            </div>

            {/* Step 2 */}
            <div className="text-center p-6 animate-fade-in stagger-1" data-testid="step-2">
              <div className="step-circle mx-auto mb-6">
                <Clock className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">2. Saat Seç</h3>
              <p className="text-slate-600">
                Müsait saatler arasından size uygun olan zaman dilimini seçin.
              </p>
            </div>

            {/* Step 3 */}
            <div className="text-center p-6 animate-fade-in stagger-2" data-testid="step-3">
              <div className="step-circle mx-auto mb-6">
                <Zap className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">3. Rezerve Et</h3>
              <p className="text-slate-600">
                Hızlıca rezervasyonunuzu tamamlayın ve oynamaya başlayın!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Facilities */}
      <section className="py-16 md:py-24" data-testid="popular-facilities-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-2">
                Popüler Tesisler
              </h2>
              <p className="text-slate-600">En çok tercih edilen tesisler</p>
            </div>
            <Button 
              variant="outline" 
              onClick={() => navigate('/tesisler')}
              className="hidden md:flex items-center gap-2 rounded-full"
              data-testid="view-all-facilities-btn"
            >
              Tümünü Gör
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          {popularFacilities.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {popularFacilities.map((facility, index) => (
                <Card 
                  key={facility.facility_id} 
                  className="group overflow-hidden cursor-pointer card-hover"
                  onClick={() => navigate(`/tesis/${facility.facility_id}`)}
                  data-testid={`facility-card-${index}`}
                >
                  <div className="aspect-video relative overflow-hidden">
                    <img
                      src={facility.images?.[0] || (facility.facility_type === 'hali_saha' 
                        ? 'https://images.pexels.com/photos/47730/the-ball-stadion-football-the-pitch-47730.jpeg'
                        : 'https://images.unsplash.com/photo-1546519638-68e109498ffc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxpbmRvb3IlMjBiYXNrZXRiYWxsJTIwY291cnR8ZW58MHx8fHwxNzY2NDEyMDIzfDA&ixlib=rb-4.1.0&q=85')}
                      alt={facility.name}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    <div className="absolute top-3 left-3">
                      {facility.is_partner ? (
                        <Badge className="bg-amber-500 text-white shadow-lg flex items-center gap-1 text-xs">
                          <Award className="w-3 h-3" />
                          Partner
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-slate-100 text-slate-600 shadow-sm text-xs opacity-90">
                          Partner Değil
                        </Badge>
                      )}
                    </div>
                    <div className="absolute top-3 right-3">
                      <span className="price-tag">
                        {facility.price_per_hour} ₺/saat
                      </span>
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-1">
                      <h3 className="font-bold text-lg text-slate-900 group-hover:text-emerald-700 transition-colors">
                        {facility.name}
                      </h3>
                      {facility.average_rating > 0 && (
                        <div className="flex items-center gap-1 bg-yellow-50 px-2 py-1 rounded-lg">
                          <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                          <span className="text-sm font-bold text-yellow-700">{facility.average_rating}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center text-slate-500 text-sm mb-2">
                      <MapPin className="w-4 h-4 mr-1" />
                      {facility.district}, {facility.city}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center px-2 py-1 rounded-md bg-slate-100 text-slate-700 text-xs">
                        {getFacilityTypeLabel(facility.facility_type)}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-slate-50 rounded-xl">
              <p className="text-slate-600">Henüz tesis bulunmuyor.</p>
              <Button 
                onClick={() => navigate('/tesisler')}
                className="mt-4 bg-emerald-700 hover:bg-emerald-800"
              >
                Tüm Tesisleri Gör
              </Button>
            </div>
          )}

          <div className="md:hidden mt-6 text-center">
            <Button 
              variant="outline" 
              onClick={() => navigate('/tesisler')}
              className="rounded-full"
            >
              Tümünü Gör
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 md:py-24 bg-emerald-800" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
              Neden tesiskirala?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6" data-testid="feature-1">
              <div className="w-16 h-16 bg-lime-300 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Zap className="w-8 h-8 text-emerald-800" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Hızlı Rezervasyon</h3>
              <p className="text-emerald-100">
                Saniyeler içinde tesis bulun ve rezervasyon yapın.
              </p>
            </div>

            <div className="text-center p-6" data-testid="feature-2">
              <div className="w-16 h-16 bg-lime-300 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Shield className="w-8 h-8 text-emerald-800" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Güvenli Ödeme</h3>
              <p className="text-emerald-100">
                Güvenli ödeme altyapısı ile gönül rahatlığıyla ödeyin.
              </p>
            </div>

            <div className="text-center p-6" data-testid="feature-3">
              <div className="w-16 h-16 bg-lime-300 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Users className="w-8 h-8 text-emerald-800" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Geniş Ağ</h3>
              <p className="text-emerald-100">
                Türkiye genelinde yüzlerce onaylı tesis.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div 
            className="relative rounded-3xl overflow-hidden p-8 md:p-16"
            style={{
              backgroundImage: 'url(https://images.unsplash.com/photo-1533739331049-c6a02504f54f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxmcmllbmRzJTIwcGxheWluZyUyMHNwb3J0c3xlbnwwfHx8fDE3NjY0MTIwMjR8MA&ixlib=rb-4.1.0&q=85)',
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          >
            <div className="absolute inset-0 bg-emerald-900/80"></div>
            <div className="relative z-10 text-center max-w-2xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
                Tesisinizi Listeleyin
              </h2>
              <p className="text-lg text-emerald-100 mb-8">
                Spor tesisiniz mi var? Platformumuza katılın ve binlerce potansiyel müşteriye ulaşın.
              </p>
              <Button 
                onClick={() => navigate('/giris')}
                className="bg-lime-300 hover:bg-lime-400 text-emerald-900 font-bold px-8 py-6 text-lg rounded-full"
                data-testid="cta-list-facility-btn"
              >
                İşletmeci Olarak Başlayın
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
